// src/contexts/AuthContext.js (Updated to ensure user document creation)
import React, { createContext, useContext, useEffect, useReducer } from 'react';
import { authService } from '../services/authService';
import { userService } from '../services/userService';

const AuthContext = createContext();

// Auth Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_USER':
      return { ...state, user: action.payload, loading: false };
    case 'SET_USER_PROFILE':
      return { ...state, userProfile: action.payload };
    case 'CLEAR_USER':
      return { ...state, user: null, userProfile: null, loading: false };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    default:
      return state;
  }
};

const initialState = {
  user: null,
  userProfile: null,
  loading: true,
  error: null
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    dispatch({ type: 'SET_LOADING', payload: true });

    const unsubscribe = authService.onAuthStateChanged(async (user) => {
      try {
        if (user) {
          dispatch({ type: 'SET_USER', payload: user });
          
          // ALWAYS create/update user profile in parrotdocs-users collection
          const userProfile = await userService.createUserProfile(user);
          dispatch({ type: 'SET_USER_PROFILE', payload: userProfile });
        } else {
          dispatch({ type: 'CLEAR_USER' });
        }
      } catch (error) {
        console.error('Auth state change error:', error);
        dispatch({ type: 'SET_ERROR', payload: error.message });
      }
    });

    return unsubscribe;
  }, []);

  // Authentication methods
  const signUp = async (email, password, displayName) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = await authService.signUpWithEmail(email, password, displayName);
      // User profile will be created automatically by the auth state listener
      return user;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  const signIn = async (email, password) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = await authService.signInWithEmail(email, password);
      // User profile will be updated automatically by the auth state listener
      return user;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = await authService.signInWithGoogle();
      // User profile will be created/updated automatically by the auth state listener
      return user;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await authService.signOut();
      dispatch({ type: 'CLEAR_USER' });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  const resetPassword = async (email) => {
    try {
      await authService.resetPassword(email);
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  const updateProfile = async (displayName, photoURL) => {
    try {
      await authService.updateUserProfile(displayName, photoURL);
      
      // Refresh user profile
      const userProfile = await userService.getUserProfile(state.user.uid);
      dispatch({ type: 'SET_USER_PROFILE', payload: userProfile });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  const updateProfileInfo = async (profileData) => {
    try {
      await userService.updateProfileInfo(state.user.uid, profileData);
      
      // Refresh user profile
      const userProfile = await userService.getUserProfile(state.user.uid);
      dispatch({ type: 'SET_USER_PROFILE', payload: userProfile });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  const updatePreferences = async (preferences) => {
    try {
      await userService.updateUserPreferences(state.user.uid, preferences);
      
      // Refresh user profile
      const userProfile = await userService.getUserProfile(state.user.uid);
      dispatch({ type: 'SET_USER_PROFILE', payload: userProfile });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  const refreshUserProfile = async () => {
    if (!state.user?.uid) return null;
    const userProfile = await userService.getUserProfile(state.user.uid);
    dispatch({ type: 'SET_USER_PROFILE', payload: userProfile });
    return userProfile;
  };

  const clearError = () => {
    dispatch({ type: 'SET_ERROR', payload: null });
  };

  const value = {
    ...state,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resetPassword,
    updateProfile,
    updateProfileInfo,
    updatePreferences,
    refreshUserProfile,
    clearError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
